import { toast } from "react-toastify"; // Importing toast notifications for user feedback
import routes from "../../../Routes/routes"; // Importing application routes
import { CreateListEmployee, UploadEmployeeFormFile } from "../../../reduxToolkit/Files/FilesApi"; // Importing API actions for employee file upload and creation
import { useEffect } from "react";
import {errorMsg} from "../../../Apis/functions"; // React hook to handle side effects

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

// This function manages the logic for uploading, validating, and creating employee records with error handling
export const CreationErrorsEmployeeApp = ({
                                              t, // Translation function
                                              dispatch, // Redux dispatch
                                              navigate, // Router navigation function
                                              spreadsheetData, // Uploaded spreadsheet data
                                              data, // Processed data ready for submission
                                              setUpdate, // Function to update state when uploading
                                              getFormData, // Function to prepare the FormData object
                                              isValidFile, // Callback to validate uploaded file data
                                              jspreadsheet, // JSpreadsheet instance for rendering tables
                                              zoneData, // Zone data for rendering in spreadsheet
                                              jRefZone, // Ref for the zone spreadsheet
                                              roleData, // Role data for rendering in spreadsheet
                                              jRefRole, // Ref for the role spreadsheet
                                              departmentData, // Department data for rendering in spreadsheet
                                              jRefDepartment, // Ref for the department spreadsheet
                                              jRefNoError, // Ref for displaying valid records
                                          }) => {

    // Function to handle file upload logic
    const handleUploadFile = () => {
        setUpdate(true); // Trigger a state update (e.g., to show loading UI)

        if (spreadsheetData) { // Check if there is spreadsheet data
            let data = getFormData(); // Get the FormData from the file input
            if (data) {
                dispatch(UploadEmployeeFormFile(getFormData())) // Upload the file via Redux action
                    .then(({ payload: { data: data, success } }) => {
                        if (success === true) {
                            isValidFile(data); // Call validation callback if successful
                        }
                    })
                    .catch(() => {
                        toast.error(t('fail_to_upload_file').toUpperCase()); // Show error toast on failure
                    });
            }
        } else {
            toast?.warn(t('there_is_not_file_to_upload').toUpperCase()); // Warn if no file is provided
        }
    };

    // Function to handle employee list creation after upload and validation
    const handleConfirm = () => {
        dispatch(CreateListEmployee(data)) // Create employee records via Redux action
            .then(({ payload: { data: { success, code } } }) => {
                if (success === true) {
                    toast.success(t('has_been_created_successfully')); // Show success toast
                    navigate(routes.employee.list); // Redirect to the employee list page
                } else errorMsg(code)
            });
    };

    // Render the zone data in a spreadsheet table when it is available
    useEffect(() => {
        if (zoneData && jRefZone.current) {
            jspreadsheet(jRefZone.current, {
                data: zoneData,
                minDimensions: [2, 2],
                tableOverflow: true,
                columns: new Array(2).fill({ type: 'text', width: 130 }),
            });
        }
    }, [zoneData]);

    // Render the role data in a spreadsheet table when it is available
    useEffect(() => {
        if (roleData && jRefRole.current) {
            jspreadsheet(jRefRole.current, {
                data: roleData,
                minDimensions: [2, 2],
                tableOverflow: true,
                columns: new Array(2).fill({ type: 'text', width: 130 }),
            });
        }
    }, [roleData]);

    // Render the department data in a spreadsheet table when it is available
    useEffect(() => {
        if (departmentData && jRefDepartment.current) {
            jspreadsheet(jRefDepartment.current, {
                data: departmentData,
                minDimensions: [2, 2],
                tableOverflow: true,
                columns: new Array(2).fill({ type: 'text', width: 130 }),
            });
        }
    }, [departmentData]);

    // Render valid records without errors in a spreadsheet
    useEffect(() => {
        if (data && jRefNoError.current) {
            jspreadsheet(jRefNoError.current, {
                data: data,
                minDimensions: [2, 2],
                tableOverflow: true,
                columns: new Array(2).fill({ type: 'text', width: 170 }),
            });
        }
    }, [data]);

    // Return handler functions to be used in the UI
    return {
        handleConfirm,
        handleUploadFile,
    };
};

// Export the logic container to be used in a corresponding UI component
export default CreationErrorsEmployeeApp;
